// seedQualityHints.js — advisory-only "seed quality" hints for players whose
// 2025 season was injury-shortened (gms <= 10), where the default alloc seed
// (buildInitAllocs: raw season share into a 17-game-pace slider PLUS a games
// slider) double-counts availability and misstates the player's ROLE.
//
// Vault-validated (docs/projection-stability-research.md, 2026-07-17 section;
// full study docs/TPRR_Role_Stability.md + addenda in the vault):
// - Corrected backtest (outcome = next-year WHEN-ACTIVE share): for g<=10
//   seasons, when-active pace is the best single role indicator (MAE 5.70 vs
//   raw 6.67; blends with prior year ~5.4-5.7, n=43-76).
// - Verified-injury cohort: every starter-level (share >=15%) g<=10 WR season
//   2021-24 was a genuine injury (29/29 — "maybe benched" pollution lives
//   below 14% share). Young/same-team injured starters barely decay: −1.9pp
//   mean next-year when-active share.
// - The RISK is availability, not role: after a <=10-game season, 47% miss 4+
//   games again next year (vs 29% baseline); mean next-year games 12.7.
// - The exception profile that reliably LOSES the role is injury year + TEAM
//   CHANGE: WR movers at any age kept >=80% of role only 1/6 times (mean
//   −9.0pp); RB 27+ movers −18.1pp (1/5 kept role); but sub-27 RB movers were
//   neutral-to-POSITIVE (+6.7pp, 5/6 kept role — the acquiring team paid for
//   the role: CMC→SF, Michel→LA, Dobbins→LAC). Mover cells are n=4-6 —
//   advisory only, NEVER auto-seed from them.
//
// whenActive measurement caveat: whenActive = rawShare × 17/gms is an
// APPROXIMATION that can OVERSTATE — it assumes uniform team volume across
// games. FPD's directly-measured when-active TGT% for Nabers 2025 is 24.4%
// (targets in his active games ÷ team targets in those same games); the study's
// ×17/g proxy read ~31% on its denominators, and this module's passAtt
// denominator reads ~28% — still overstated. All copy must present whenActive
// as approximate ("~"). The parked seed fix will use FPD when-active shares
// directly instead of this proxy.
//
// Source confirmation: PLAYER_HISTORY is RAW nflverse season counting stats
// (gen_player_history.py / working_tab.csv — targets, rushAtt, gms), NOT
// FPD-style when-active shares, so applying ×17/g to shares computed from it
// is valid (no double-count). FPD's season TGT%/RTE% are ALREADY when-active
// denominated — if a future data source swaps those in, drop the transform.
//
// RB mover age gate: the PLAYERS pool carries an `age` field, used to split
// RB movers at 27 (>=27 warn, <27 info/opportunity). A pool miss for an
// individual RB falls back to the warn-level kind with age: null.
//
// The SEED FIX itself (seeding when-active blends + sub-role-mean games) is
// PARKED for a deliberate re-baseline session — it moves projections. This
// module ships the advisory layer only: computeSeedQualityHints is a pure
// read of teamStarters + PLAYER_HISTORY + TEAM_HISTORY + PLAYERS and never
// mutates allocs.

import { TEAM_HISTORY as PLAY_HISTORY } from "../data/projectionData";
import { PLAYERS } from "../data/players";
import { SLOTS, findPlayerHist } from "./projlabProjection";

// Same defensive normalization as competitionHints.js.
const TEAM_NORM = { JAC: "JAX", LAR: "LA" };
const normTeam = (t) => TEAM_NORM[t] ?? t;

// Same suffix-strip regex family as findPlayerHist (projlabProjection.js).
function stripSuffix(name) {
  return name.replace(/\s+(Jr\.?|Sr\.?|II|III|IV|V)$/i, "").trim();
}

// Primary stat only — tgt for WR/TE, rush for RB (mirrors what buildInitAllocs
// seeds for each position's dominant slider).
const STAT_FIELDS = {
  WR: { stat: "tgt",  playerField: "targets", teamField: "passAtt" },
  TE: { stat: "tgt",  playerField: "targets", teamField: "passAtt" },
  RB: { stat: "rush", playerField: "rushAtt", teamField: "rushAtt" },
};

const round1 = (v) => Math.round(v * 10) / 10;

// Pool age lookup for the RB mover split, keyed name|pos (suffix-stripped).
const poolAge = new Map();
for (const p of PLAYERS) {
  if (p.age != null) poolAge.set(`${stripSuffix(p.name).toLowerCase()}|${p.pos}`, p.age);
}

function teamYearRec(team, year) {
  return (PLAY_HISTORY[team] || []).find(y => y.year === year) ?? null;
}

// Player count ÷ team-season total, as a percent. Null on any missing data.
function sharePct(count, teamRec, teamField) {
  if (count == null || !teamRec || !teamRec[teamField]) return null;
  return round1(count / teamRec[teamField] * 100);
}

// 2024 share (any team, vs THAT team's 2024 totals). priorTeam is tagged only
// when the 2024 team differs from the team the 2025 share is anchored to.
function priorShareOf(hist, playerField, teamField, anchorTeam) {
  const rec = hist.find(h => h.year === 2024);
  if (!rec) return { priorShare: null, priorTeam: null };
  const team = normTeam(rec.team);
  const share = sharePct(rec[playerField] || 0, teamYearRec(team, 2024), teamField);
  if (share == null) return { priorShare: null, priorTeam: null };
  return { priorShare: share, priorTeam: team === anchorTeam ? null : team };
}

/**
 * computeSeedQualityHints(abbrev, teamStarters) -> { [slotKey]: hint }
 *
 * Named skill slots only (RB1-2 / WR1-3 / TE1-2). No hint when gms is
 * missing/zero or the primary-stat share is unresolvable. Two branches:
 *
 * 1. Same-team short season — the player's 2025 same-team record (the record
 *    buildInitAllocs seeds from) has gms <= 10:
 *    { level:"info", kind:"short-season-seed", pos, stat, gms, statCount,
 *      teamTotal, rawShare, whenActive, priorShare, priorTeam, reference }
 *    reference = avg(whenActive, priorShare) when prior exists, else whenActive.
 *
 * 2. Moved after an injury-shortened year — NO same-team 2025 record, but a
 *    2025 record on ANOTHER team with gms <= 10 (the crater profile branch 1
 *    silently misses). Same fields computed against the OLD team's 2025
 *    totals, plus movedFrom (and age for RBs). Level/kind:
 *    WR/TE any age -> warn "short-season-moved"; RB age >= 27 (or age
 *    unknown) -> warn "short-season-moved"; RB age < 27 -> info
 *    "short-season-moved-young-rb".
 */
export function computeSeedQualityHints(abbrev, teamStarters) {
  const hints = {};
  const curTeam = normTeam(abbrev);

  for (const s of SLOTS) {
    if (s.isRemainder || s.pos === "QB") continue;
    const slotData = teamStarters?.[s.key];
    if (!slotData?.player) continue;
    const hist = findPlayerHist(slotData.player, slotData.sleeper_id);
    if (!hist?.length) continue;

    const { stat, playerField, teamField } = STAT_FIELDS[s.pos];
    const recs2025 = hist.filter(h => h.year === 2025);
    const sameTeamRec = recs2025.find(h => normTeam(h.team) === curTeam);

    if (sameTeamRec) {
      // Branch 1: same-team short season.
      if (!sameTeamRec.gms || sameTeamRec.gms > 10) continue;
      const teamRec = teamYearRec(curTeam, 2025);
      const statCount = sameTeamRec[playerField];
      const rawShare = sharePct(statCount, teamRec, teamField);
      if (!rawShare) continue; // 0-share seasons seed from slotavg/capital, not this record
      const whenActive = round1(rawShare * 17 / sameTeamRec.gms);
      const { priorShare, priorTeam } = priorShareOf(hist, playerField, teamField, curTeam);
      const reference = priorShare != null ? round1((whenActive + priorShare) / 2) : whenActive;
      hints[s.key] = {
        level: "info", kind: "short-season-seed", pos: s.pos, stat,
        gms: sameTeamRec.gms, statCount, teamTotal: teamRec[teamField],
        rawShare, whenActive, priorShare, priorTeam, reference,
      };
      continue;
    }

    // Branch 2: moved after an injury-shortened year.
    const movedRec = recs2025.find(h => h.gms && h.gms <= 10);
    if (!movedRec) continue;
    const oldTeam = normTeam(movedRec.team);
    const teamRec = teamYearRec(oldTeam, 2025);
    const statCount = movedRec[playerField];
    const rawShare = sharePct(statCount, teamRec, teamField);
    if (!rawShare) continue;
    const whenActive = round1(rawShare * 17 / movedRec.gms);
    const { priorShare, priorTeam } = priorShareOf(hist, playerField, teamField, oldTeam);
    const reference = priorShare != null ? round1((whenActive + priorShare) / 2) : whenActive;

    let level = "warn", kind = "short-season-moved", age = null;
    if (s.pos === "RB") {
      age = poolAge.get(`${stripSuffix(slotData.player).toLowerCase()}|RB`) ?? null;
      if (age != null && age < 27) { level = "info"; kind = "short-season-moved-young-rb"; }
    }
    hints[s.key] = {
      level, kind, pos: s.pos, stat,
      gms: movedRec.gms, statCount, teamTotal: teamRec[teamField],
      rawShare, whenActive, priorShare, priorTeam, reference,
      movedFrom: oldTeam, age,
    };
  }

  return hints;
}
